import React from 'react'
import { Alert, Button, Form, Input, Radio } from 'antd'
import { useIntl } from 'react-intl'
import { useMutation, useQuery } from 'graphql-hooks'

import { trialSurveyQuestions } from '../../graphql/custom-queries'
import { MAX_SURVEY_TEXT_LENGTH } from '@vacationtracker/shared/constants/survey'
import { ISurveyVariantConfig } from './survey-variants'

import './trial-survey-form.less'

interface IGqlShowIf {
  questionId: string
  equals?: boolean | null
  equalsValue?: string | null
}

interface IGqlOption {
  value: string
  labelKey: string
  allowText?: boolean | null
}

interface IGqlQuestion {
  id: string
  type: 'text' | 'boolean' | 'choice'
  labelKey: string
  order: number
  required: boolean
  showIf?: IGqlShowIf | null
  options?: IGqlOption[] | null
  surveyVersion: number
}

interface ITrialSurveyQuestionsData {
  trialSurveyQuestions: IGqlQuestion[]
}

interface IAnswerVariable {
  questionId: string
  textValue?: string
  boolValue?: boolean
  choiceValue?: string
  otherText?: string
}

interface ITrialSurveyFormProps {
  config: ISurveyVariantConfig
  onSubmitted: (result: { showCalendlyCta?: boolean; trialEnd?: string }) => void
}

type FormValues = Record<string, string | boolean>

const otherFieldName = (questionId: string): string => `${questionId}__other`

function showIfExpected(showIf: IGqlShowIf): string | boolean {
  return showIf.equalsValue !== null && showIf.equalsValue !== undefined ? showIf.equalsValue : (showIf.equals as boolean)
}

function isVisible(question: IGqlQuestion, values: FormValues): boolean {
  if (!question.showIf) return true
  return values[question.showIf.questionId] === showIfExpected(question.showIf)
}

function selectedOption(question: IGqlQuestion, values: FormValues): IGqlOption | undefined {
  return (question.options ?? []).find((o) => o.value === values[question.id])
}

function isAnswered(question: IGqlQuestion, values: FormValues): boolean {
  const value = values[question.id]
  if (question.type === 'boolean') return typeof value === 'boolean'
  if (question.type === 'choice') {
    if (typeof value !== 'string' || value.length === 0) return false
    const option = selectedOption(question, values)
    if (option?.allowText) {
      const other = values[otherFieldName(question.id)]
      return typeof other === 'string' && other.trim().length > 0
    }
    return true
  }
  return typeof value === 'string' && value.trim().length > 0
}

// The backend returns a stable error code as the GraphQL error message
// (AppSync forces errorType to "Lambda:Unhandled"); map it to a localized key.
function errorMessageKey(error: unknown, config: ISurveyVariantConfig): string {
  const code = (error as { graphQLErrors?: Array<{ message?: string }> })?.graphQLErrors?.[0]?.message
  if (code && code in config.errorCodeToKey) {
    return config.errorCodeToKey[code]
  }
  return config.fallbackErrorKey
}

export function TrialSurveyForm({ config, onSubmitted }: ITrialSurveyFormProps): JSX.Element | null {
  const { formatMessage } = useIntl()
  const {
    loading,
    data,
    error: queryError,
  } = useQuery<ITrialSurveyQuestionsData>(trialSurveyQuestions, {
    variables: { surveyName: config.surveyName },
  })
  const [submitMutation, { loading: submitting, error: submitError }] = useMutation(config.mutation)
  const [form] = Form.useForm()
  const [formValues, setFormValues] = React.useState<FormValues>({})
  const [submitFailedMessage, setSubmitFailedMessage] = React.useState<string | null>(null)

  if (loading || !data) return null
  if (queryError) return null

  const questions = [...data.trialSurveyQuestions].sort((a, b) => a.order - b.order)

  const allRequiredAnswered = questions.filter((q) => q.required && isVisible(q, formValues)).every((q) => isAnswered(q, formValues))

  const onValuesChange = (_changed: unknown, allValues: FormValues): void => {
    setFormValues(allValues)
  }

  const toAnswer = (q: IGqlQuestion, values: FormValues): IAnswerVariable => {
    if (q.type === 'text') return { questionId: q.id, textValue: String(values[q.id] ?? '') }
    if (q.type === 'boolean') return { questionId: q.id, boolValue: Boolean(values[q.id]) }
    // Only send the "other" text when the selected option actually allows it;
    // the field stays mounted, so it can hold a value from a prior selection.
    const other = selectedOption(q, values)?.allowText ? values[otherFieldName(q.id)] : undefined
    return {
      questionId: q.id,
      choiceValue: String(values[q.id] ?? ''),
      otherText: other != null ? String(other) : undefined,
    }
  }

  const onFinish = async (values: FormValues): Promise<void> => {
    setSubmitFailedMessage(null)
    const answers = questions.filter((q) => isVisible(q, values)).map((q) => toAnswer(q, values))

    const { data: result, error: mutationError } = await submitMutation({ variables: { answers } })
    const payload = result?.[config.resultKey]
    if (mutationError || !payload) {
      setSubmitFailedMessage(formatMessage({ id: errorMessageKey(mutationError, config) }))
      return
    }
    onSubmitted(payload)
  }

  const renderInput = (q: IGqlQuestion): JSX.Element => {
    if (q.type === 'text') {
      return <Input.TextArea maxLength={MAX_SURVEY_TEXT_LENGTH} rows={4} />
    }
    if (q.type === 'choice') {
      return (
        <Radio.Group>
          {(q.options ?? []).map((o) => (
            <Radio key={o.value} value={o.value} style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
              {formatMessage({ id: o.labelKey })}
            </Radio>
          ))}
        </Radio.Group>
      )
    }
    return (
      <Radio.Group>
        <Radio value={true}>{formatMessage({ id: `${config.i18nPrefix}.boolean.yes` })}</Radio>
        <Radio value={false}>{formatMessage({ id: `${config.i18nPrefix}.boolean.no` })}</Radio>
      </Radio.Group>
    )
  }

  return (
    <Form className="trial-survey-extend-form" form={form} layout="vertical" onFinish={onFinish} onValuesChange={onValuesChange}>
      {questions.map((q) => {
        const option = q.type === 'choice' ? selectedOption(q, formValues) : undefined
        return (
          <React.Fragment key={q.id}>
            <Form.Item
              name={q.id}
              label={formatMessage({ id: q.labelKey })}
              rules={[
                {
                  required: q.required && isVisible(q, formValues),
                  message: formatMessage({ id: `${config.i18nPrefix}.error.required` }),
                },
              ]}
              hidden={!isVisible(q, formValues)}
            >
              {renderInput(q)}
            </Form.Item>
            {q.type === 'choice' && (q.options ?? []).some((o) => o.allowText) && (
              // Kept mounted (hidden when not applicable) so its value persists
              // and stays in sync with the disabled-submit check - same as every
              // other conditional field, which use `hidden` rather than unmounting.
              <Form.Item name={otherFieldName(q.id)} style={{ marginTop: -8 }} hidden={!(option?.allowText && isVisible(q, formValues))}>
                <Input.TextArea rows={3} placeholder={formatMessage({ id: `${config.i18nPrefix}.otherPlaceholder` })} maxLength={MAX_SURVEY_TEXT_LENGTH} />
              </Form.Item>
            )}
          </React.Fragment>
        )
      })}
      {submitFailedMessage && <Alert type="error" message={submitFailedMessage} style={{ marginBottom: 16 }} />}
      {submitError && !submitFailedMessage && <Alert type="error" message={formatMessage({ id: config.fallbackErrorKey })} style={{ marginBottom: 16 }} />}
      <Button type="primary" htmlType="submit" loading={submitting} disabled={!allRequiredAnswered} block>
        {formatMessage({ id: `${config.i18nPrefix}.submit` })}
      </Button>
    </Form>
  )
}
